import { Component, ChangeDetectorRef, inject } from '@angular/core';
import { Router, ActivatedRoute } from '@angular/router';
import { BackHeaderComponent } from '@app/back-header/back-header.component';
import { CommonModule } from '@angular/common';
import { MatCardModule } from '@angular/material/card';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatDividerModule } from '@angular/material/divider';
import { MatListModule } from '@angular/material/list';
import { DocumentsApiService } from '@app/services/documents-api.service';
import { DocumentsInterface } from '@app/interface/documents';
import { Organizations } from '@app/interface/organizations';
import { OrganizationApi } from '@app/services/organization-api.service';
import { RecursiveInterface } from '@app/services/base-authenticated.service';
import { PaymentInterface } from '@app/interface/payment';
import { ViewPaymentDialogComponent } from '@app/view-payment-dialog/view-payment-dialog.component';
import { MatDialog } from '@angular/material/dialog';
import { SubscriptionInterface } from '@app/interface/subscription';
import { PaymentApiService } from '@app/services/payment-api.service';
import { SubscriptionApiService } from '@app/services/subscription-api.service';
import {
  Guide1DetailsDialogComponent,
  Guide2DetailsDialogComponent,
  Guide3DetailsDialogComponent,
  Guide4DetailsDialogComponent,
  Guide5DetailsDialogComponent,
  Guide6DetailsDialogComponent,
} from '@app/guide-details-dialog/guide-details-dialog.component';
import { ToastService } from '@app/services/toast.service';

// Define the shape of your tutorial object
interface GuideItem {
  id: number;
  title: string;
  subtitle: string;
  content: string;
  // Glifo Material mostrato nella tessera petrol in cima alla card: da' alla
  // griglia un punto di riconoscimento per ogni guida oltre al numero.
  icon: string;
}

@Component({
  selector: 'izy-guides-and-faq-section',
  imports: [
    BackHeaderComponent,
    CommonModule,
    MatCardModule,
    MatButtonModule,
    MatIconModule,
    MatProgressSpinnerModule,
    MatDividerModule,
    MatListModule,
  ],
  templateUrl: './guides-and-faq-section.component.html',
  styleUrl: './guides-and-faq-section.component.scss',
})
export class GuidesAndFaqSectionComponent {
  private toast = inject(ToastService);
  guidesList: DocumentsInterface[] = [];
  isLoading = true;
  organization_data: Organizations | null = null;

  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private cdr: ChangeDetectorRef,
    private organizationService: OrganizationApi,
    private dialog: MatDialog,
  ) {}

  // HARDCODED DATA: 6 Tutorials
  tutorials: GuideItem[] = [
    {
      id: 1,
      title: 'Primi passi',
      subtitle: 'Come iniziare rapidamente a fatturare',
      content:
        'Scopri come configurare il tuo account e inserire i primi dati per partire subito.',
      icon: 'rocket_launch',
    },
    {
      id: 2,
      title: 'Emettere Fatture',
      subtitle: 'Creazione documenti e bozze',
      content:
        'La guida completa per creare, modificare ed inviare la tua prima fattura elettronica allo SDI.',
      icon: 'receipt_long',
    },
    {
      id: 3,
      title: 'Gestione Clienti e Prestazioni',
      subtitle: 'Anagrafiche',
      content:
        'Impara a gestire la rubrica clienti e prestazioni salvate per velocizzare la compilazione dei documenti.',
      icon: 'contacts',
    },
    {
      id: 4,
      title: 'Gli stati di invio di una fattura',
      subtitle: "Capire se l'invio di ogni fattura è andato a buon fine o meno",
      content:
        'Una volta creata una fattura, subisce una serie di controlli per verificare che tutto sia corretto. Tramite lo stato, sai a che punto è del processo.',
      icon: 'fact_check',
    },
    {
      id: 5,
      title: 'Regime Forfettario e Scadenze Fiscali',
      subtitle: 'Calendario, Tasse e limiti',
      content:
        'Non perdere le date importanti: guida al calendario fiscale e ai versamenti INPS.',
      icon: 'calendar_month',
    },
    {
      id: 6,
      title: 'Assistenza e Gestione account',
      subtitle: 'Supporto umano e aiuto',
      content:
        'Come contattare il supporto tecnico, modificare impostazioni del tuo account o contattare il tuo Commercialista direttamente dalla piattaforma.',
      icon: 'support_agent',
    },
  ];

  ngOnInit(): void {
    this.getOrganization();
    this.isLoading = false;
  }

  private getOrganization(): void {
    const organization_id = Number(
      this.route.snapshot.paramMap.get('organizationId'),
    );

    if (organization_id && !isNaN(organization_id)) {
      const filterParams = {
        organization_id: organization_id,
      };

      this.organizationService
        .getOrganization<Organizations>(organization_id, filterParams)
        .subscribe({
          next: (organization_data: Organizations) => {
            this.organization_data = organization_data;
            this.cdr.detectChanges();
          },
          error: (err: any) => {
            console.error('Error loading organization:', err);
            this.toast.error("Errore nel caricamento dell'organizzazione");
            this.cdr.detectChanges();
          },
        });
    } else {
      console.error(
        'Could not extract valid organization ID from URL or route parameters',
      );
    }
  }

  public sourceLabels: Record<string, string> = {
    BEIZY: 'Beizy',
    'CAF UIL Toscana': 'CAF UIL Toscana',
    OTHER: 'fatturazione',
  };

  // ROUTING LOGIC: Decides which dialog to open based on ID
  handleGuideClick(id: number): void {
    switch (id) {
      case 1:
        this.openGuide1Dialog();
        break;
      case 2:
        this.openGuide2Dialog();
        break;
      case 3:
        this.openGuide3Dialog();
        break;
      case 4:
        this.openGuide4Dialog();
        break;
      case 5:
        this.openGuide5Dialog();
        break;
      case 6:
        this.openGuide6Dialog();
        break;
      default:
        this.showErrorMessage(
          'Dettagli per questa guida non ancora disponibili.',
        );
        break;
    }
  }

  openGuide1Dialog() {
    const dialogRef = this.dialog.open(Guide1DetailsDialogComponent, {
      width: '90vw',
      maxWidth: '1000px', // Slightly wider to accommodate the link
      maxHeight: '700px',
      /* Stile in styles.scss (.izy-guide-dialog-panel): il dialog vive
         nell'overlay CDK, fuori dai componenti. Stessa grammatica del dialog
         di conferma 'Modifiche non salvate'. */
      panelClass: 'izy-guide-dialog-panel',
      data: {
        organizationId: this.organization_data?.id,
        organization: this.organization_data,
      },
    });
  }

  openGuide2Dialog() {
    const dialogRef = this.dialog.open(Guide2DetailsDialogComponent, {
      width: '90vw',
      maxWidth: '1000px', // Slightly wider to accommodate the link
      maxHeight: '700px',
      /* Stile in styles.scss (.izy-guide-dialog-panel): il dialog vive
         nell'overlay CDK, fuori dai componenti. Stessa grammatica del dialog
         di conferma 'Modifiche non salvate'. */
      panelClass: 'izy-guide-dialog-panel',
      data: {
        organizationId: this.organization_data?.id,
        organization: this.organization_data,
      },
    });
  }

  openGuide3Dialog() {
    const dialogRef = this.dialog.open(Guide3DetailsDialogComponent, {
      width: '90vw',
      maxWidth: '1000px', // Slightly wider to accommodate the link
      maxHeight: '700px',
      /* Stile in styles.scss (.izy-guide-dialog-panel): il dialog vive
         nell'overlay CDK, fuori dai componenti. Stessa grammatica del dialog
         di conferma 'Modifiche non salvate'. */
      panelClass: 'izy-guide-dialog-panel',
      data: {
        organizationId: this.organization_data?.id,
        organization: this.organization_data,
      },
    });
  }

  openGuide4Dialog() {
    const dialogRef = this.dialog.open(Guide4DetailsDialogComponent, {
      width: '90vw',
      maxWidth: '1000px', // Slightly wider to accommodate the link
      maxHeight: '700px',
      /* Stile in styles.scss (.izy-guide-dialog-panel): il dialog vive
         nell'overlay CDK, fuori dai componenti. Stessa grammatica del dialog
         di conferma 'Modifiche non salvate'. */
      panelClass: 'izy-guide-dialog-panel',
      data: {
        organizationId: this.organization_data?.id,
        organization: this.organization_data,
      },
    });
  }

  openGuide5Dialog() {
    const dialogRef = this.dialog.open(Guide5DetailsDialogComponent, {
      width: '90vw',
      maxWidth: '1000px', // Slightly wider to accommodate the link
      maxHeight: '700px',
      /* Stile in styles.scss (.izy-guide-dialog-panel): il dialog vive
         nell'overlay CDK, fuori dai componenti. Stessa grammatica del dialog
         di conferma 'Modifiche non salvate'. */
      panelClass: 'izy-guide-dialog-panel',
      data: {
        organizationId: this.organization_data?.id,
        organization: this.organization_data,
      },
    });
  }

  openGuide6Dialog() {
    const dialogRef = this.dialog.open(Guide6DetailsDialogComponent, {
      width: '90vw',
      maxWidth: '1000px', // Slightly wider to accommodate the link
      maxHeight: '700px',
      /* Stile in styles.scss (.izy-guide-dialog-panel): il dialog vive
         nell'overlay CDK, fuori dai componenti. Stessa grammatica del dialog
         di conferma 'Modifiche non salvate'. */
      panelClass: 'izy-guide-dialog-panel',
      data: {
        organizationId: this.organization_data?.id,
        organization: this.organization_data,
      },
    });
  }

  backToDashboard(): void {
    const org_id = this.route.snapshot.paramMap.get('organizationId');
    this.router.navigate([`/${org_id}/dashboard`]);
  }

  private showErrorMessage(message: string): void {
    this.toast.error(message);
  }
}
